import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GameComponentProps } from '../core/types';
import { evaluateClambersSubmission, getClambersCompletionDelta } from './logic';
import { isClambersPuzzle, type ClambersPuzzle } from './types';

type ClambersSavedState = {
  version: 1;
  input: string;
  rejectionCount: number;
  replayCount: number;
  permanentReveal: boolean;
};

type PlaybackStage = 'watch' | 'blank' | 'letter' | 'input';

const PLAYBACK_DURATION_MS = 3_000;
const LETTER_SLOT_MS = PLAYBACK_DURATION_MS / 6;
const LETTER_VISIBLE_MS = 440;

function readSavedState(value: unknown): ClambersSavedState {
  if (!value || typeof value !== 'object') {
    return { version: 1, input: '', rejectionCount: 0, replayCount: 0, permanentReveal: false };
  }
  const candidate = value as Partial<ClambersSavedState>;
  return {
    version: 1,
    input:
      typeof candidate.input === 'string'
        ? candidate.input
            .toLowerCase()
            .replace(/[^a-z]/g, '')
            .slice(0, 6)
        : '',
    rejectionCount:
      Number.isInteger(candidate.rejectionCount) && candidate.rejectionCount! >= 0
        ? candidate.rejectionCount!
        : 0,
    replayCount:
      Number.isInteger(candidate.replayCount) && candidate.replayCount! >= 0
        ? candidate.replayCount!
        : 0,
    permanentReveal: candidate.permanentReveal === true,
  };
}

export function ClambersGame({
  puzzle,
  savedState,
  disabled,
  onStateChange,
  onCharge,
  onComplete,
}: GameComponentProps) {
  if (!isClambersPuzzle(puzzle.payload)) {
    throw new Error('Clambers received an invalid puzzle payload.');
  }
  const clambers = puzzle.payload as ClambersPuzzle;
  const initial = useMemo(() => readSavedState(savedState), [savedState]);
  const [input, setInput] = useState(initial.input);
  const [rejectionCount, setRejectionCount] = useState(initial.rejectionCount);
  const [replayCount, setReplayCount] = useState(initial.replayCount);
  const [permanentReveal, setPermanentReveal] = useState(initial.permanentReveal);
  const [playbackRevision, setPlaybackRevision] = useState(0);
  const [stage, setStage] = useState<PlaybackStage>(initial.permanentReveal ? 'input' : 'watch');
  const [visibleLetter, setVisibleLetter] = useState('');
  const [controlsReady, setControlsReady] = useState(initial.permanentReveal);
  const [playing, setPlaying] = useState(!initial.permanentReveal);
  const [status, setStatus] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const persist = useCallback(
    (nextInput: string, nextRejections: number, nextReplays: number, nextPermanent: boolean) => {
      onStateChange({
        version: 1,
        input: nextInput,
        rejectionCount: nextRejections,
        replayCount: nextReplays,
        permanentReveal: nextPermanent,
      });
    },
    [onStateChange],
  );

  useEffect(() => {
    if (permanentReveal) {
      setStage('input');
      setPlaying(false);
      setControlsReady(true);
      return;
    }
    setStage('watch');
    setVisibleLetter('');
    setControlsReady(false);
    setPlaying(true);
    const handles: number[] = [];
    const schedule = (callback: () => void, delay: number) => {
      handles.push(window.setTimeout(callback, delay));
    };
    [...clambers.letterOrder].forEach((letter, index) => {
      const start = index * LETTER_SLOT_MS;
      schedule(() => {
        setVisibleLetter(letter);
        setStage('letter');
      }, start);
      schedule(() => setStage('blank'), start + LETTER_VISIBLE_MS);
    });
    schedule(() => {
      setStage('input');
      setControlsReady(true);
      setPlaying(false);
      setStatus('Enter any valid anagram');
      window.requestAnimationFrame(() => inputRef.current?.focus());
    }, PLAYBACK_DURATION_MS);
    return () => handles.forEach((handle) => window.clearTimeout(handle));
  }, [clambers.letterOrder, permanentReveal, playbackRevision]);

  const controlsLocked = disabled || playing || !controlsReady;

  const updateInput = (value: string) => {
    if (controlsLocked) return;
    const next = value
      .toLowerCase()
      .replace(/[^a-z]/g, '')
      .slice(0, 6);
    setInput(next);
    setStatus('');
    persist(next, rejectionCount, replayCount, permanentReveal);
  };

  const submit = () => {
    if (controlsLocked) return;
    const result = evaluateClambersSubmission(input, clambers);
    if (result.kind === 'incomplete') {
      setStatus('Enter six letters');
      return;
    }
    if (result.kind === 'wrong-letters' || result.kind === 'not-accepted') {
      const nextCount = rejectionCount + 1;
      onCharge(`rejected-${nextCount}`, 10_000, 'Rejected Clambers answer');
      setRejectionCount(nextCount);
      setStatus(
        result.kind === 'wrong-letters'
          ? 'Use exactly the shown letters · +10s'
          : 'Not an accepted anagram · +10s',
      );
      persist(input, nextCount, replayCount, permanentReveal);
      return;
    }
    const hasPenalty = rejectionCount > 0 || replayCount > 0 || permanentReveal;
    onComplete({
      kind: 'solved',
      baseDeltaMs: getClambersCompletionDelta(hasPenalty),
      message: 'Unscrambled!',
      metadata: {
        answer: result.answer,
        sourceWord: clambers.sourceWord,
        rejectionCount,
        replayCount,
        permanentReveal,
      },
    });
  };

  const replay = () => {
    if (controlsLocked) return;
    const nextCount = replayCount + 1;
    onCharge(`replay-${nextCount}`, 10_000, 'Replay Clambers letters');
    setReplayCount(nextCount);
    setInput('');
    setStatus('Letters replaying · +10s');
    persist('', rejectionCount, nextCount, false);
    setPlaybackRevision((revision) => revision + 1);
  };

  const revealPermanently = () => {
    if (controlsLocked || permanentReveal) return;
    onCharge('permanent-reveal', 30_000, 'Reveal Clambers letters');
    setPermanentReveal(true);
    setStatus('Letters revealed · +30s');
    persist(input, rejectionCount, replayCount, true);
  };

  return (
    <section className="clambers-game" aria-labelledby="clambers-title">
      <p className="eyebrow">Memorize six letters · find a valid anagram</p>
      <h1 id="clambers-title" className="sr-only">
        Clambers
      </h1>

      <div className="clambers-stage" aria-live="assertive" aria-atomic="true">
        {permanentReveal ? (
          <div className="clambers-letter-row" aria-label="Revealed letters">
            {[...clambers.letterOrder].map((letter, index) => (
              <span key={`${letter}-${index}`}>{letter}</span>
            ))}
          </div>
        ) : stage === 'watch' ? (
          <strong>Watch here</strong>
        ) : stage === 'letter' ? (
          <strong className="clambers-flash-letter">{visibleLetter}</strong>
        ) : (
          <span aria-hidden="true">&nbsp;</span>
        )}
      </div>

      {stage === 'input' && (
        <form
          className="clambers-form"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <label htmlFor="clambers-answer">Your six-letter anagram</label>
          <div className="clambers-input-row">
            <input
              ref={inputRef}
              id="clambers-answer"
              value={input}
              maxLength={6}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              disabled={controlsLocked}
              onChange={(event) => updateInput(event.target.value)}
            />
            <button type="submit" disabled={controlsLocked || input.length !== 6}>
              Submit
            </button>
          </div>
        </form>
      )}

      <div className="clambers-hints" aria-label="Clambers hints">
        <button type="button" disabled={controlsLocked || permanentReveal} onClick={replay}>
          Show letters again <span>(+10s)</span>
        </button>
        <button
          type="button"
          disabled={controlsLocked || permanentReveal}
          onClick={revealPermanently}
        >
          {permanentReveal ? 'Letters revealed' : 'Reveal letters'}{' '}
          <span>{permanentReveal ? '(used)' : '(+30s)'}</span>
        </button>
      </div>
      <p className="clambers-status" role="status" aria-live="polite">
        {status}
      </p>
    </section>
  );
}
